import { css, CSSObject, CSSProperties } from 'styled-components';

export const colorCode = {
  Gainsboro: '#DEDEDF',
  Crayola: '#157EFB',
  FireBrick: '#BC2121',
  LaSalleGreen: '#067C21',
  TrueBlue: '#0456D9',
  MikadoYellow: '#FBBD08',
};

export const borderColor = {
  primaryBorder: colorCode.Gainsboro,
  blueBorder: colorCode.Crayola,
};

export const notificationColors = {
  notificationError: colorCode.FireBrick,
  notificationSuccess: colorCode.LaSalleGreen,
  notificationInfo: colorCode.TrueBlue,
  notificationWarning: colorCode.MikadoYellow,
};

export const b2cColors = {
  b2cDarkWhite: '#F2F2F2',
  b2cLightGrey: '#CCCCCC',
  b2cGrey: '#666666',
  b2cDarkGrey: '#4D4D4D',
  b2cDarkBlack: '#1A1A1A',
  b2cBlack: '#333333',
  b2cRedThl: '#CD2E0A',
  b2cRed: '#B3232C',
  b2cAlertRed: '#E52823',
  b2cDarkRed: '#A30C33',
  b2cLightRed: '#FDF7F5',
  b2cLightBlue: '#0882CB',
  b2cWhite: '#FFFFFF',
  b2cLightOrange: '#DE4A24',
};

export const colors = {
  backgroundGray: 'hsl(240, 18%, 97%)',
  borderGray: 'hsl(0, 0%, 75%)',
  borderLightgray: 'hsla(0, 0%, 0%, 0.08)',
  textColor: 'hsl(228, 16%, 13%)',
  selectedBackground: 'hsla(213, 97%, 53%, 0.05)',
  invertedMain: 'hsl(223, 3%, 44%)',
  cardBorder: 'hsla(0, 0%, 0%, 0.08)',
  cardMain: 'hsl(227, 22%, 16%)',
  blueMain: 'hsl(213, 97%, 53%)',
  shadow: 'hsla(0, 0%, 0%, 0.3)',
  r2d2Blue: '#157efb',
  chinaBlue: '#1174ea',
  darkChinaBlue: '#0d65cd',
  doubleHappySmoke: '#f5f5f8',
  selectionBlue: '#eaf5fd',
  mouseGrey: '#ececf0',
  primerGrey: '#bebebe',
  grey: '#767676',
  battleshipGrey: '#6c6e73',
  nightBattleshipGrey: '#1b1d25',
  outline: '#7bb5fa',
  errorRed: '#f10d1d',
  warningYellow: '#efbc38',
  successGreen: '#417504',
  white: 'hsl(0, 100%, 100%)',
  black: 'hsl(0, 0%, 0%)',
  darkRed: '#bc2121',
  darkBlue: '#115db9',
  darkGreen: '#067c21',
  gold: '#fbbd08',
  orange: '#f2711c',
  green: '#21ba45',
  red: '#db2828',
  lightGreen: '#effdf2',
  lightRed: '#ffefef',
  halfBlack: 'hsla(0, 0%, 0%, 0.5)',
  thirdBlack: 'hsla(0, 0%, 0%, 0.3)',
  grey60: 'hsl(0, 0%, 60%)',
  invertedPrimerGrey: 'hsl(0, 0%, 50%)',
  invertedDefault: 'hsl(225, 5%, 30%)',
  darkGray: 'hsl(0, 0%, 24%)',
  roadBearRed: '#a62836',
  roadBearYellow: '#e8a311',
  elMonteRed: '#e51b24',
  elMonteYellow: '#fcd006',
  mightyRed: '#CE2E23',
  mightyMud: '#534D03',
  britzOrangeDark: '#C85900',
  justGoRed: '#A60C2D',
  bunkCampersBlue: '#09A6D5',
  apolloUkOrange: '#F5822B',
  /** new design */
  breadcrumbGray: '#707070',
  tableHoverBlue: '#eef9ff',
  tableBorderGray: '#dededf',
  textBlack: '#333333',
  textGray: '#87888c',
  textLightGray: '#cccccc',
  textWhite: '#FFFFFF',
  sidepanelGray: '#B5B5B5',
  neutralGray: '#F0F0F0',
  black2: '#1B1C1D',
  breadcrumbBlack: '#000000',
  errorRedBorder: '#bf0000',
  successGreenBorder: '#21BA45',
  lightGreyBackground: '#fafafa',
  ...borderColor,
  ...notificationColors,
  ...b2cColors,
};

interface Size {
  [key: string]: {
    width: string;
    height: string;
  };
}

export const size: Size = {
  small: {
    width: '1em',
    height: '1em',
  },
  small3: {
    width: '1.2em',
    height: '1.2em',
  },
  small2: {
    width: '1.5em',
    height: '1.5em',
  },
  medium: {
    width: '3em',
    height: '3em',
  },
  large: {
    width: '5em',
    height: '5em',
  },
};

const fonts = {
  openSans: 'Open Sans',
};

interface Overrides {
  [key: string]: CSSObject | Overrides;
}

type ThemeShape = {
  input?: CSSObject;
  button?: CSSObject;
  select?: CSSObject;
};

export type ThemeType = {
  name: string;
  colors?: typeof colors;
  size?: typeof size;
  fonts?: typeof fonts;
  overrides?: Overrides;
  shape?: ThemeShape;
};

export const defaultTheme: ThemeType = {
  name: 'DEFAULT',
  colors,
  size,
  fonts,
};

const camelCaseToHyphen = (key: string): string =>
  key.replace(/([A-Z])/g, (g) => `-${g[0].toLowerCase()}`);

type ShapeKey = keyof CSSProperties;
const shapeKeys: ShapeKey[] = [
  'fontWeight',
  'border',
  'borderTop',
  'borderBottom',
  'borderLeft',
  'borderRight',
  'borderRadius',
  'padding',
  'paddingTop',
  'paddingBottom',
  'paddingLeft',
  'paddingRight',
  'margin',
  'marginTop',
  'marginBottom',
  'marginLeft',
  'marginRight',
];

const getThemingShape = (
  theme: ThemeType,
  target: keyof ThemeShape,
  defaultCSS?: CSSObject,
) => (key: ShapeKey) => {
  const value = theme?.shape?.[target]?.[key] ?? defaultCSS?.[key];

  return value ? `${camelCaseToHyphen(key)}: ${value};` : '';
};

export const mixinThemingShape = (
  target: keyof ThemeShape,
  defaultCSS?: CSSObject,
) => css`
  ${({ theme }) => shapeKeys.map(getThemingShape(theme, target, defaultCSS))};
`;
